import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Screen } from "@/components/layout/Screen";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/interactions/EmptyState";
import { useActivities } from "@/hooks/api/useActivities";
import {
  Clock,
  Zap,
  ClipboardList,
  ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { format, parseISO } from "date-fns";

// Map activity_type enum values to display info
const activityDisplayMap: Record<string, { emoji: string; label: string; color: string }> = {
  feeding: { emoji: "🐔", label: "Feeding", color: "text-warning bg-warning/10" },
  vaccination: { emoji: "💉", label: "Vaccination", color: "text-info bg-info/10" },
  weight_sampling: { emoji: "⚖️", label: "Weighing", color: "text-primary bg-primary/10" },
  shed_cleaning: { emoji: "🧹", label: "Shed Cleaning", color: "text-success bg-success/10" },
  medication: { emoji: "💊", label: "Medication", color: "text-warning bg-warning/10" },
  biosecurity_check: { emoji: "🔍", label: "Biosecurity Check", color: "text-info bg-info/10" },
  planting: { emoji: "🌱", label: "Planting", color: "text-success bg-success/10" },
  harvesting: { emoji: "🌾", label: "Harvesting", color: "text-warning bg-warning/10" },
  weeding: { emoji: "🪴", label: "Weeding", color: "text-success bg-success/10" },
  spraying: { emoji: "💧", label: "Spraying", color: "text-info bg-info/10" },
  fertilizing: { emoji: "🧪", label: "Fertilizing", color: "text-primary bg-primary/10" },
  irrigation: { emoji: "🚿", label: "Irrigation", color: "text-info bg-info/10" },
  soil_preparation: { emoji: "🪨", label: "Soil Preparation", color: "text-muted-foreground bg-muted" },
  other: { emoji: "📋", label: "Other", color: "text-muted-foreground bg-muted" },
};

const getActivityDisplay = (type: string) =>
  activityDisplayMap[type] ?? { emoji: "📋", label: type.replace(/_/g, " "), color: "text-muted-foreground bg-muted" };

export const ActivityLogScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: activities = [], isLoading } = useActivities();

  // Group activities by date
  const groupedActivities = useMemo(() => {
    const groups: Record<string, typeof activities> = {};
    for (const activity of activities) {
      const dateKey = activity.date;
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(activity);
    }
    // Sort dates descending
    return Object.entries(groups).sort(([a], [b]) => b.localeCompare(a));
  }, [activities]);

  const formatDateHeading = (dateStr: string) => {
    try {
      const date = parseISO(dateStr);
      const today = new Date();
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);

      if (format(date, "yyyy-MM-dd") === format(today, "yyyy-MM-dd")) return t("common.today");
      if (format(date, "yyyy-MM-dd") === format(yesterday, "yyyy-MM-dd")) return t("common.yesterday");
      return format(date, "EEE, d MMM yyyy");
    } catch {
      return dateStr;
    }
  };

  return (
    <Screen
      showTopBar={true}
      showBottomNav={true}
      topBarProps={{ title: t("activity.title", { defaultValue: "Activity Log" }), showBack: true, onBack: () => navigate("/") }}
    >
      <div className="p-4 space-y-6 pb-24">
        {/* Loading state */}
        {isLoading && (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-20 w-full rounded-xl" />
                <Skeleton className="h-20 w-full rounded-xl" />
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!isLoading && activities.length === 0 && (
          <EmptyState
            icon={ClipboardList}
            title={t("activity.noActivities", { defaultValue: "No activities yet" })}
            description={t("activity.noActivitiesDesc", { defaultValue: "Your logged activities will appear here" })}
          />
        )}

        {/* Activities grouped by date */}
        {!isLoading && groupedActivities.map(([dateKey, dateActivities]) => (
          <div key={dateKey} className="space-y-2">
            {/* Date heading */}
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                {formatDateHeading(dateKey)}
              </h3>
              <Badge variant="secondary" size="sm">
                {t("activity.count", { count: dateActivities.length, defaultValue: "{{count}} activities" })}
              </Badge>
            </div>

            {/* Activity cards for this date */}
            {dateActivities.map((activity) => {
              const display = getActivityDisplay(activity.type);
              return (
                <Card key={activity.id} elevation="low" className="p-4">
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className={cn("w-10 h-10 rounded-full flex items-center justify-center text-lg shrink-0", display.color)}>
                      {display.emoji}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground">{t(`cbs.activityTypes.${activity.type}`, { defaultValue: display.label })}</p>
                      {activity.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">{activity.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {activity.date}
                        </span>
                        {activity.points > 0 && (
                          <span className="flex items-center gap-1 text-warning">
                            <Zap className="w-3 h-3" />
                            {activity.points} {t("common.points", { defaultValue: "pts" })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        ))}
      </div>
    </Screen>
  );
};
